// Frontend navigation metrics -> Folio backend (/v1/{slug}/track) -> Granometrics.
//
// The browser never holds the gateway credentials; it posts events to our own
// tenant-scoped endpoint, which forwards them server-side (thin proxy). This
// mirrors alice-imersiva's split (interface emits, grano.js forwards): here the
// batching/buffering/beacon logic lives client-side, the ingest client backend.
//
// Tracking is gated on config.grano_enabled — call initAnalytics(enabled, ...)
// once (from ConfigContext) after /config resolves. Until then events are dropped.
//
// Visits: every event carries a session_id. A visit starts at the Start button
// or at the first touch after a silence (session_gap_seconds, admin Config
// page) and ends at the next such silence, at the Start screen, or at the idle
// reset. Each end is one "Visita" event (slot 9) with how long it was active,
// so Granometrics can count visitors even when the screen never went idle.

import { getConfig } from "../../context/ConfigContext";
import { getSlug, getDeviceKey } from "../../tenant";

// Fixed for every Folio web event (the backend enforces these too); kept here
// only so the payload the backend receives is already well-formed.
const SENSOR_TYPE = 3;
const SENSOR_EVENT = 2;

// sensor_id slots (agreed taxonomy).
export const SLOT = {
  HOME: 1,
  VIDEO: 2,
  FACHADA: 3,
  PLANTA: 4,
  AREAS_COMUNS: 5,
  LIVING: 6,
  ATRACAO: 7,
  QRCODE: 8,
  VISIT: 9, // a visit ended: how long it was active, and why it ended
};

const BUFFER_KEY = "magicFolioGranoQueue";
const SESSION_KEY_PREFIX = "magicFolioSession:";
const MAX_BATCH = 250; // mirrors grano.js
const FLUSH_AFTER_MS = 5000; // debounce window
const FLUSH_AT_SIZE = 20; // flush early once the queue reaches this
// Offline for long: keep the newest events, drop the oldest beyond this
// (~1 MB of localStorage; a box is a few hundred events a day).
const MAX_QUEUE = 4000;
// Admin Config values (session_gap_seconds / idle_timeout_seconds) until /config lands.
const DEFAULT_GAP_MS = 90 * 1000;
const DEFAULT_IDLE_MS = 600 * 1000;

let enabled = false;
let queue = readBuffer();
let flushTimer = null;
// One batch on the wire at a time: two overlapping successes would each drop a
// slice from the head and lose events that were never sent.
let inFlight = false;
let flushPending = false;
// The last POST failed (offline, or the server said retry): beacons are skipped
// until a fetch succeeds again, so a page hide never empties the queue blindly.
let lastFlushFailed = false;
// Empreendimento on screen, added to every event so Granometrics can split a
// gateway's navigation per empreendimento. The backend stamps its name.
let empreendimentoId = null;

// The visit in progress ({ id, startedAt, lastActivityAt, taps }), null between
// visitors. Persisted per tenant: one left behind by a reload or a kiosk restart
// is closed as "restart" once tracking is initialised (it needs `enabled`).
let session = null;
let leftoverSession = readSession();
let gapMs = DEFAULT_GAP_MS;
let idleMs = DEFAULT_IDLE_MS;
let sessionPersistTimer = null;

// Browser debug: turn on with `localStorage.granoDebug = "1"` (then reload).
// Logs every enqueue + the actual Granometrics ingest response echoed by the
// backend, so you can verify acceptance from the console/Network tab.
function debugOn() {
  try {
    return localStorage.getItem("granoDebug") === "1";
  } catch (e) {
    return false;
  }
}

function readBuffer() {
  try {
    const raw = JSON.parse(localStorage.getItem(BUFFER_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];
  }
}

function persist() {
  try {
    localStorage.setItem(BUFFER_KEY, JSON.stringify(queue));
  } catch (e) {
    /* ignore quota / disabled storage */
  }
}

function sessionKey() {
  const slug = getSlug();
  return slug ? SESSION_KEY_PREFIX + slug : null;
}

function readSession() {
  try {
    const key = sessionKey();
    const raw = key ? JSON.parse(localStorage.getItem(key)) : null;
    return raw && raw.id && raw.startedAt && raw.lastActivityAt ? raw : null;
  } catch (e) {
    return null;
  }
}

function persistSession() {
  try {
    const key = sessionKey();
    if (!key) return;
    if (session) localStorage.setItem(key, JSON.stringify(session));
    else localStorage.removeItem(key);
  } catch (e) {
    /* ignore quota / disabled storage */
  }
}

// A playing video reports activity several times a second: write at most every 5 s.
function persistSessionSoon() {
  if (sessionPersistTimer) return;
  sessionPersistTimer = setTimeout(() => {
    sessionPersistTimer = null;
    persistSession();
  }, 5000);
}

// Local wall-clock "YYYY-MM-DD HH:MM:SS.mmm" — the format grano.js sends.
function localStamp(d = new Date()) {
  const p = (n, w = 2) => String(n).padStart(w, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`
  );
}

// Duration buckets as the label, so Granometrics' plain category counts already
// give the "how long do people use it" histogram; exact seconds ride in custom.
function activeBucket(seconds) {
  if (seconds < 60) return "até 1 min";
  if (seconds < 180) return "1 a 3 min";
  if (seconds < 300) return "3 a 5 min";
  if (seconds < 600) return "5 a 10 min";
  if (seconds < 1200) return "10 a 20 min";
  return "mais de 20 min";
}

// A Magic Folio box tags its events with its key (?device=): the backend
// routes them to the box's own Granometrics gateway when it has one, and
// stamps the box's name on them otherwise.
function trackUrl() {
  const slug = getSlug();
  if (!slug) return null;
  const device = getDeviceKey();
  return getConfig().baseURLAPI + "/v1/" + slug + "/track" + (device ? "?device=" + encodeURIComponent(device) : "");
}

// Set by the screens that show one empreendimento (detail, QR landing); null clears.
// Not named setEmpreendimento: Empreendimento.jsx has a state setter by that name.
export function setTrackedEmpreendimento(id) {
  empreendimentoId = Number(id) || null;
}

// Enqueue one event. `custom` carries the per-event detail (file/address) plus
// empreendimento_id; the backend stamps client_name and the empreendimento name.
// `at` (ms epoch) backdates the event, for a visit that ended before now.
export function emit(sensorId, address, label, custom, at) {
  if (!sensorId || !address) return;
  if (!enabled) {
    // Most common "nothing happens" cause: config returned grano_enabled=false
    // (creds/toggle not saved for this client) so tracking never turned on.
    if (debugOn())
      console.log("[grano] DROPPED — tracking disabled (grano_enabled false):", sensorId, address);
    return;
  }
  const event = {
    sensor_id: sensorId,
    sensor_type: SENSOR_TYPE,
    sensor_event: SENSOR_EVENT,
    sensor_address: address,
    sensor_label: label || "",
    timestamp: localStamp(at ? new Date(at) : new Date()),
    custom: {
      ...(custom || {}),
      ...(empreendimentoId ? { empreendimento_id: empreendimentoId } : {}),
      ...(session ? { session_id: session.id } : {}),
    },
  };
  queue.push(event);
  if (!inFlight && queue.length > MAX_QUEUE) {
    const dropped = queue.length - MAX_QUEUE;
    queue.splice(0, dropped);
    if (debugOn()) console.warn("[grano] queue capped, dropped oldest", dropped);
  }
  persist();
  if (debugOn()) console.log("[grano] enqueue", event, "(queue:", queue.length + ")");

  if (queue.length >= FLUSH_AT_SIZE) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_AFTER_MS);
  }
}

// POST a batch to the tenant-scoped track endpoint. Uses fetch (not the shared
// axios instance) so this module never pulls Services/Axios at import time — that
// would call getConfig() during ConfigContext's own evaluation and TDZ-crash the
// app. On success drop the sent slice; on failure keep it buffered for retry.
export function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!enabled || queue.length === 0) return;
  if (inFlight) {
    flushPending = true;
    return;
  }

  const url = trackUrl();
  if (!url) return;

  const slice = queue.slice(0, MAX_BATCH);
  inFlight = true;
  flushPending = false;
  if (debugOn()) console.log("[grano] flushing", slice.length, "events →", url);
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ events: slice }),
    keepalive: true,
  })
    .then(async (res) => {
      if (!res.ok) throw new Error("track " + res.status);
      if (debugOn()) {
        let body = null;
        try {
          body = await res.clone().json();
        } catch (e) {
          /* ignore */
        }
        // body.ingest carries the real Granometrics response (status + accepted/
        // duplicates), so you can confirm acceptance from the browser.
        console.log("[grano] sent", slice.length, "→ granometrics:", body && body.ingest);
      }
      lastFlushFailed = false;
      queue = queue.slice(slice.length);
      persist();
    })
    .catch((err) => {
      // Offline, or the backend answered non-2xx (it says 502 when Granometrics
      // itself failed): keep buffered; the online/60 s/next-emit triggers retry.
      lastFlushFailed = true;
      if (debugOn()) console.warn("[grano] flush failed (kept buffered):", err.message);
    })
    .then(() => {
      inFlight = false;
      const again = flushPending;
      flushPending = false;
      if (!lastFlushFailed && (queue.length > 0 || again)) flush(); // drain the rest
    });
}

// Best-effort synchronous flush for page unload — sendBeacon survives navigation
// where an async axios POST would be cancelled. Used for the QR-redirect too.
export function beaconFlush() {
  if (!enabled || queue.length === 0) return;
  // sendBeacon() only says "queued", never "delivered": offline (or right after a
  // failed flush) it would swallow the events. Keep them; the online/60 s flush
  // or the next page load ships them. A batch in flight already has keepalive.
  if (inFlight || navigator.onLine === false || lastFlushFailed) return;
  const url = trackUrl();
  if (!url || !navigator.sendBeacon) {
    flush();
    return;
  }
  const slice = queue.slice(0, MAX_BATCH);
  const blob = new Blob([JSON.stringify({ events: slice })], { type: "application/json" });
  const ok = navigator.sendBeacon(url, blob);
  if (ok) {
    queue = queue.slice(slice.length);
    persist();
  }
}

// --- Visits (sessions) ------------------------------------------------------

function newSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function startSession(at) {
  session = { id: newSessionId(), startedAt: at, lastActivityAt: at, taps: 0 };
  persistSession();
  if (debugOn()) console.log("[grano] visit start", session.id);
}

// Close the visit in progress as one "Visita" event. endedBy: gap (silence,
// closed retroactively at the last activity) | home (back on the Start screen)
// | timeout (the idle reset) | restart (page reloaded / kiosk restarted).
function endSession(endedBy, at) {
  if (!session) return;
  const end = at || Date.now();
  const activeSeconds = Math.max(0, Math.round((session.lastActivityAt - session.startedAt) / 1000));
  const sessionSeconds = Math.max(activeSeconds, Math.round((end - session.startedAt) / 1000));
  emit(
    SLOT.VISIT,
    "Visita",
    activeBucket(activeSeconds),
    {
      ended_by: endedBy,
      active_seconds: activeSeconds, // visit start -> last touch (or playing video)
      session_seconds: sessionSeconds, // visit start -> end of the visit
      taps: session.taps,
      gap_seconds: Math.round(gapMs / 1000),
      idle_seconds: Math.round(idleMs / 1000),
    },
    end
  );
  if (debugOn())
    console.log("[grano] visit end", session.id, endedBy, activeSeconds + "s active,", session.taps, "taps");
  session = null;
  persistSession();
}

// Any user input, or a playing video ("video" never opens a visit by itself:
// the Start screen's attract loop is not a visitor). Splits visits on the gap.
function activity(kind) {
  const now = Date.now();
  if (session) {
    if (gapMs > 0 && now - session.lastActivityAt >= gapMs) {
      endSession("gap", session.lastActivityAt);
      startSession(now);
    }
  } else if (kind !== "video") {
    startSession(now);
  }
  if (!session) return;
  session.lastActivityAt = now;
  if (kind === "tap") session.taps += 1;
  if (kind === "video") persistSessionSoon();
  else persistSession();
}

// Called once from ConfigContext after /config resolves. settings:
// { gapSeconds, idleSeconds } from the admin Config page.
export function initAnalytics(isEnabled, settings) {
  enabled = !!isEnabled;
  if (settings) {
    if (Number(settings.gapSeconds) >= 0) gapMs = Number(settings.gapSeconds) * 1000;
    if (Number(settings.idleSeconds) > 0) idleMs = Number(settings.idleSeconds) * 1000;
  }
  if (leftoverSession) {
    // Ended when the page died; its last activity is the best end time we have.
    const current = session;
    session = leftoverSession;
    leftoverSession = null;
    endSession("restart", session.lastActivityAt);
    session = current;
    persistSession();
  }
  if (debugOn()) console.log("[grano] init — enabled =", enabled, "gap", gapMs, "ms idle", idleMs, "ms");
  if (enabled && queue.length > 0) flush();
}

// Flush what's queued when the tab is backgrounded/closed — and, on a box
// that sat offline, as soon as the network is back and every minute after,
// so the buffer drains without waiting for the next touch.
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", () => activity("tap"), { passive: true });
  window.addEventListener("keydown", () => activity("key"), { passive: true });
  window.addEventListener("wheel", () => activity("wheel"), { passive: true });
  // media events do not bubble; capture sees every <video> on the page
  document.addEventListener("timeupdate", () => activity("video"), true);

  window.addEventListener("online", () => setTimeout(flush, 2000));
  setInterval(() => {
    if (queue.length > 0 && navigator.onLine) flush();
  }, 60 * 1000);
  window.addEventListener("pagehide", beaconFlush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") beaconFlush();
  });
}

// --- Named helpers (taxonomy) --------------------------------------------

export const track = {
  // Start button: a new visit (the pointerdown on the button usually opened it already).
  home: () => {
    if (!session) startSession(Date.now());
    emit(SLOT.HOME, "Home button", "Start", {});
  },
  // The Start screen is showing again (Home/logo button): the visit is over.
  startScreen: () => endSession("home"),
  // The detail page's idle timer fired: the visit is over, the screen resets.
  idle: () => endSession("timeout"),
  video: (legend, file) => emit(SLOT.VIDEO, "Video", legend, { file }),
  fachada: (legend, file) => emit(SLOT.FACHADA, "Fachada", legend, { file }),
  planta: (legend, file) => emit(SLOT.PLANTA, "Planta", legend, { file }),
  areasComuns: (legend, file) => emit(SLOT.AREAS_COMUNS, "Galeria 1", legend, { file }),
  living: (legend, file) => emit(SLOT.LIVING, "Galeria 2", legend, { file }),
  atracao: (title, address) => emit(SLOT.ATRACAO, "Atração", title, { address }),
  qr: (url) => emit(SLOT.QRCODE, "QRcode", "Scan", { address: url }),
};
